import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, AlertCircle, Wrench, Info, X } from "lucide-react";
import axios from "axios";

const DISMISS_KEY = "prenotoo-status-dismissed";
const POLL_MS = 60_000;

const SEVERITY = {
  info:        { Icon: Info,           bg: "bg-blue-600",   accent: "bg-blue-700",   label: "Info" },
  maintenance: { Icon: Wrench,         bg: "bg-orange-600", accent: "bg-orange-700", label: "Manutenzione" },
  warning:     { Icon: AlertTriangle,  bg: "bg-amber-500",  accent: "bg-amber-600",  label: "Avviso" },
  critical:    { Icon: AlertCircle,    bg: "bg-rose-700",   accent: "bg-rose-800",   label: "Critico" },
};

/**
 * Sticky top banner that shows active maintenance/incident notices.
 *
 * - Fetches /api/status/active every minute (audience-filtered server-side)
 * - Non-critical notices are dismissable; dismissal persisted by `id+version`
 *   so an updated notice re-appears
 * - Critical notices cannot be dismissed
 * - Hidden inside `?embed=1` widget context
 */
export default function StatusBanner() {
  const [notices, setNotices] = useState([]);
  const [dismissed, setDismissed] = useState({});
  const location = useLocation();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) setDismissed(JSON.parse(raw) || {});
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    let alive = true;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    async function load() {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${base}/api/status/active`, { headers, timeout: 8000 });
        if (alive) setNotices(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (alive) setNotices([]);
      }
    }
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (new URLSearchParams(location.search).get("embed") === "1") return null;

  const visible = notices.filter((n) => {
    if (!n.show_banner) return false;
    if (n.severity === "critical") return true;
    const v = dismissed[n.id];
    return !(v && v >= (n.version || 1));
  });
  if (visible.length === 0) return null;

  function dismiss(n) {
    const next = { ...dismissed, [n.id]: n.version || 1 };
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }

  return (
    <div className="sticky top-0 z-[55] flex flex-col" data-testid="status-banner">
      {visible.map((n) => {
        const cfg = SEVERITY[n.severity] || SEVERITY.info;
        const Icon = cfg.Icon;
        return (
          <div
            key={`${n.id}-${n.version || 1}`}
            className={`${cfg.bg} text-white text-sm`}
            role="status"
            data-testid={`status-banner-${n.severity}`}
          >
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-start gap-3">
              <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{cfg.label}</span>
                  <span className="font-semibold">{n.title}</span>
                  {n.ends_at && (
                    <span className="text-xs opacity-80 hidden md:inline">
                      · fino al {fmt(n.ends_at)}
                    </span>
                  )}
                </div>
                <div className="text-xs opacity-90 line-clamp-2 mt-0.5 whitespace-pre-line">{n.message}</div>
              </div>
              {n.severity !== "critical" && (
                <button
                  onClick={() => dismiss(n)}
                  className={`shrink-0 h-7 w-7 rounded-full ${cfg.accent} hover:bg-black/20 inline-flex items-center justify-center`}
                  aria-label="Nascondi avviso"
                  data-testid={`status-banner-dismiss-${n.id.slice(0,8)}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
